import React, {
  ComponentProps,
  forwardRef,
  memo,
  ReactNode,
  useMemo,
} from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { isFunction } from '@surglogs/util.is-function'

import { Checkmark, Minus } from '@surglogs-app/icons'
import { isTransElement } from '@surglogs-app/util'

import { VisuallyHidden } from '../../accessibility/VisuallyHidden'
import { StyleProps } from '../styleProps/StylePropsService'

import { Box } from './Box'
import { TextBold } from './Text'

export const CheckboxSizes = ['default', 'mini'] as const
export type CheckboxSize = typeof CheckboxSizes[number]

interface CheckboxStyleProps extends StyleProps {
  disabled?: boolean
  checked?: boolean
  invalid?: boolean
  warning?: boolean
  indeterminate?: boolean
}

const IconContainer = styled(Box)<CheckboxStyleProps>`
  display: inline-block;
  vertical-align: middle;
  border: 1px solid;
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  color: ${({ theme, disabled }) =>
    disabled ? theme.ui.colors['navy.50'] : theme.ui.colors['white.100']};
  border-color: ${({ theme }) => theme.ui.colors['navy.25']};
  flex-shrink: 0;

  ${({ theme, checked, indeterminate, disabled }) =>
    checked || indeterminate
      ? css`
          background-color: ${theme.ui.colors['blue.100']};
          border-color: ${theme.ui.colors[disabled ? 'navy.10' : 'blue.100']};
        `
      : ''}

  ${({ theme, warning, checked }) =>
    warning &&
    css`
      border-color: ${theme.ui.colors[checked ? 'blue.100' : 'orange.100']};
    `}

  ${({ theme, disabled }) =>
    disabled &&
    css`
      background-color: ${theme.ui.colors['navy.10']};
    `}


  ${({ theme, invalid, checked }) =>
    invalid &&
    css`
      border-color: ${theme.ui.colors[checked ? 'blue.100' : 'red.100']};
    `};
`

const CheckboxContainer = styled.label<CheckboxStyleProps>`
  width: ${({ w }) => (w ? w : '100%')};
  max-width: ${({ maw }) => (maw ? maw : '100%')};
  display: flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  position: relative;

  &:hover,
  &:active,
  &:focus {
    ${IconContainer} {
      ${({ theme, checked, indeterminate, disabled }) => {
        if (!disabled) {
          return css`
            border-color: ${theme.ui.colors[
              checked || indeterminate ? 'blue.75' : 'blue.100'
            ]};
            background-color: ${theme.ui.colors[
              checked || indeterminate ? 'blue.75' : 'white.100'
            ]};
          `
        }
      }}
    }
  }
`

const CheckboxText = styled(TextBold)`
  display: inline-block;
  user-select: none;
`

const SizeMap: Record<
  CheckboxSize,
  Pick<ComponentProps<typeof IconContainer>, 'w' | 'h' | 'bra'>
> = {
  mini: {
    bra: 'xs',
    w: 'md',
    h: 'md',
  },
  default: {
    bra: 'abs.6',
    w: 'lg',
    h: 'lg',
  },
}

interface CheckboxProps
  extends Omit<
    ComponentProps<typeof CheckboxContainer>,
    'size' | 'children' | 'onChange'
  > {
  children?:
    | null
    | ReactNode
    | ((props: { checked: boolean; disabled: boolean }) => ReactNode | null)
  size?: CheckboxSize
  onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined
  labelProps?: ComponentProps<typeof CheckboxText>
}

export const Checkbox = memo(
  forwardRef<HTMLInputElement, CheckboxProps>(
    (
      {
        checked = false,
        disabled = false,
        children,
        indeterminate = false,
        invalid = false,
        'aria-label': label,
        warning = false,
        size = 'default',
        onChange,
        labelProps,
        ...rest
      },
      ref,
    ) => {
      const content = useMemo(() => {
        if (!children) {
          return null
        }

        if (
          !isFunction(children) &&
          (typeof children === 'string' || isTransElement(children))
        ) {
          return (
            <CheckboxText
              {...{
                ml: 'sm',
                variant: disabled ? 'navy.50' : 'navy.100',
                ...labelProps,
              }}>
              {children}
            </CheckboxText>
          )
        }

        if (isFunction(children)) {
          return <Box {...{ ml: 'sm' }}>{children({ checked, disabled })}</Box>
        }

        return <Box {...{ ml: 'sm' }}>{children}</Box>
      }, [checked, children, disabled, labelProps])

      return (
        <CheckboxContainer
          {...{
            role: 'checkbox',
            size,
            disabled,
            indeterminate,
            checked,
            tabIndex: 0,
            'aria-label': label,
            'aria-checked': checked,
            ...rest,
          }}>
          <VisuallyHidden>
            <input
              {...{
                type: 'checkbox',
                disabled,
                checked,
                ref,
                'aria-hidden': true,
                onChange,
              }}
            />
          </VisuallyHidden>
          <IconContainer
            {...{
              checked,
              warning,
              invalid,
              disabled,
              indeterminate,
              ...SizeMap[size],
            }}>
            {checked || indeterminate ? (
              <>{checked ? <Checkmark /> : <Minus />}</>
            ) : null}
          </IconContainer>
          {content}
        </CheckboxContainer>
      )
    },
  ),
)
